import { all, call, put, select, take, takeEvery } from 'redux-saga/effects';
import { ActionType } from 'typesafe-actions';

import { catalogGet } from 'CorePackage/CatalogsModule/actions/catalogsActions';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IFilterResult } from 'APIPackage/MainModule/interfaces/IFilterResult';
import {
  getCatalogDecisionStatusesSelectOptions,
  getCatalogsTerritorialUnitsSelectOptions,
  getUserProsecutorSelectOptions,
  makeGetCatalogSelectOptions,
} from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import {
  getDictionariesKnoOrganizationsSelectOptions,
  makeGetDictionariesTargetEntitySelectOptions,
  makeGetDictionariesSubjectTypesMultiSelectOptions,
  makeGetDictionariesSubjectKindsMultiSelectOptions,
  makeGetDictionariesSubjectSubKindsMultiSelectOptions,
  getDictionariesRiskCategoriesSelectOptions,
  makeGetDictionariesSupervisionTypesExtendedSelectOptions,
  getDictionariesDecisionKindsAllSelectOptions,
  getDictionariesSubjectTypesMultiStatus,
  getDictionariesSubjectKindsMultiStatus,
  getDictionariesSubjectSubKindsMultiStatus,
} from 'CorePackage/DictionariesModule/selectors/dictionariesSelectors';
import { IDictionarySupervisionTypeSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySupervisionTypeSelectOption';
import { IDictionaryKnoOrganizationSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryKnoOrganizationSelectOption';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IKnmStatusSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmStatusSelectOption';
import { ICountrySelectOption } from 'CorePackage/CatalogsModule/interfaces/ICountrySelectOption';
import { IGenericReasonSelectOption } from 'CorePackage/CatalogsModule/interfaces/IGenericReasonSelectOption';
import { IDictionaryTargetEntitySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryTargetEntitySelectOption';
import { IDictionarySubjectTypeSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySubjectTypeSelectOption';
import { IDictionarySubjectKindSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySubjectKindSelectOption';
import { IDictionarySubjectSubKindSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySubjectSubKindSelectOption';
import { IDictionaryRiskCategorySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryRiskCategorySelectOption';
import { getStorageItem } from 'CorePackage/MainModule/common/localStore';
import { StorageNamesEnum } from 'CorePackage/MainModule/enums/StorageNamesEnum';
import {
  dictionariesKnoOrganizationsItemGet,
  dictionariesSupervisionTypesItemGet,
  dictionariesTargetEntityItemGet,
  dictionariesSubjectTypesMultiItemGet,
  dictionariesSubjectKindsMultiItemGet,
  dictionariesSubjectSubKindsMultiItemGet,
  dictionariesRiskCategoriesItemGet,
  dictionariesDecisionKindsAllItemGet,
} from 'CorePackage/DictionariesModule/actions/dictionariesActions';
import { sagaWrapper } from 'CorePackage/MainModule/common/sagaWrapper';
import { dictionariesSupervisionTypesItemGetHandlerWithReturnOptions } from 'CorePackage/DictionariesModule/sagas/dictionariesSaga';
import { IDecisionListFilterRaw } from 'DecisionListPackage/MainModule/interfaces/IDecisionListFilterRaw';
import {
  createDecisionFilter,
  deleteDecisionFilter,
  fetchDecisionFilterById,
  fetchDecisionFilters,
  updateDecisionFilter,
} from 'APIPackage/DecisionModule/services/decisionListFiltersApi';
import { IUserOrganisationSelectOption } from 'CorePackage/CatalogsModule/interfaces/IUserOrganisationSelectOption';
import { IDecisionKind } from 'APIPackage/DictionariesModule/interfaces/IDecisionKind';
import { DictionaryStatusesEnum } from 'CorePackage/DictionariesModule/enums/DictionaryStatusesEnum';
import { DictionariesActionsEnum } from 'CorePackage/DictionariesModule/enums/DictionariesActionsEnum';

import { DecisionListFilterActionsEnum } from '../enums/DecisionListFilterActionsEnum';
import {
  decisionListFilterCreate,
  decisionListFilterGetData,
  decisionListFilterGetMeta,
  decisionListFilterRemove,
  decisionListFilterSetCurrent,
  decisionListFilterSetCurrentControlOrganizations,
  decisionListFilterSetCurrentSupervisionTypes,
  decisionListFilterSetData,
  decisionListFilterSetMeta,
  decisionListFilterUpdate,
} from '../actions/decisionListFilterActions';
import { mapRawToDecisionFilter } from '../common/mapRawToDecisionFilter';
import { mapDecisionFilterToRaw } from '../common/mapDecisionFilterToRaw';
import { convertFilterDataToControlOrganizationsIds } from '../common/convertFilterDataToControlOrganizationsIds';
import { convertFilterDataToSupervisionTypesIds } from '../common/convertFilterDataToSupervisionTypesIds';
import { IDecisionListFilter } from '../interfaces/IDecisionListFilter';
import {
  getDecisionListCurrentControlOrganizations,
  getDecisionListCurrentSupervisionTypes,
} from '../selectors/decisionListFilterSelectors';
import { ITerritorialUnitSelectOption } from '../../../CorePackage/CatalogsModule/interfaces/ITerritorialUnitSelectOption';
import { IDecisionListFilterSelectOptions } from '../interfaces/IDecisionListFilterSelectOptions';

export function* watchDecisionListFilterSaga() {
  yield takeEvery(DecisionListFilterActionsEnum.DECISION_LIST_FILTER_INIT, decisionListFilterInitSaga);
  yield takeEvery(DecisionListFilterActionsEnum.DECISION_LIST_FILTER_GET_META, decisionListFilterGetMetaSaga);
  yield takeEvery(DecisionListFilterActionsEnum.DECISION_LIST_FILTER_GET_DATA, decisionListFilterGetDataSaga);
  yield takeEvery(DecisionListFilterActionsEnum.DECISION_LIST_FILTER_CREATE, decisionListFilterCreateSaga);
  yield takeEvery(DecisionListFilterActionsEnum.DECISION_LIST_FILTER_UPDATE, decisionListFilterUpdateSaga);
  yield takeEvery(DecisionListFilterActionsEnum.DECISION_LIST_FILTER_REMOVE, decisionListFilterRemoveSaga);
  yield takeEvery(
    DecisionListFilterActionsEnum.DECISION_LIST_FILTER_SET_CURRENT_CONTROL_ORGANIZATIONS,
    decisionListFilterSetCurrentControlOrganizationsSaga,
  );
  yield takeEvery(
    DecisionListFilterActionsEnum.DECISION_LIST_FILTER_SET_CURRENT_SUPERVISION_TYPES,
    decisionListFilterSetCurrentSupervisionTypesSaga,
  );
}

const decisionListPageCatalogs: CommonCatalogsNamesEnum[] = [
  CommonCatalogsNamesEnum.KNM_YEARS,
  CommonCatalogsNamesEnum.SUPERVISION_TYPES,
  CommonCatalogsNamesEnum.USER_KNOS,
  CommonCatalogsNamesEnum.DECISION_STATUSES,
  CommonCatalogsNamesEnum.KNM_TYPES_CANCELED,
  CommonCatalogsNamesEnum.COUNTRIES,
  CommonCatalogsNamesEnum.DECISION_GENERIC_REASONS,
  CommonCatalogsNamesEnum.TERRITORIAL_UNITS,
];

function* decisionListFilterInitSaga() {
  const state: IRootState = yield select();

  yield all(decisionListPageCatalogs.map(catalogName => put(catalogGet(catalogName))));
  yield put(dictionariesDecisionKindsAllItemGet());
  yield put(dictionariesTargetEntityItemGet(undefined));
  yield put(dictionariesRiskCategoriesItemGet(undefined, undefined, undefined, undefined));
  yield put(dictionariesKnoOrganizationsItemGet());
  const controllingOrganizations = getDecisionListCurrentControlOrganizations(state);
  const supervisionTypes = getDecisionListCurrentSupervisionTypes(state);

  if (controllingOrganizations.length) {
    yield put(dictionariesSupervisionTypesItemGet(controllingOrganizations, false));
  }
  if (controllingOrganizations.length && supervisionTypes.length) {
    yield put(dictionariesSubjectTypesMultiItemGet(controllingOrganizations, supervisionTypes));
    yield put(dictionariesSubjectKindsMultiItemGet(controllingOrganizations, supervisionTypes));
    yield put(dictionariesSubjectSubKindsMultiItemGet(controllingOrganizations, supervisionTypes));
  }
  yield put(decisionListFilterGetMeta());
}

function* decisionListFilterGetMetaSaga() {
  function* funcTry() {
    const meta: IFilterResult[] = yield fetchDecisionFilters();

    yield put(decisionListFilterSetMeta(meta));
    const current = getStorageItem<IDecisionListFilter>(StorageNamesEnum.DECISION_FILTER);

    if (current?.controllingOrganizations) {
      const ids: string[] = current?.controllingOrganizations
        .map(item => (typeof item.value !== 'string' ? item.value.dictVersionId : ''))
        .filter(item => item);

      yield put(decisionListFilterSetCurrentControlOrganizations(ids));
    }

    if (current?.supervisionType) {
      yield put(decisionListFilterSetCurrentSupervisionTypes([current.supervisionType.value.dictVersionId]));
    }

    const currentId = meta.find(m => m.name === current?.name)?.id;

    yield put(decisionListFilterSetCurrent({ current, currentId }));
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* decisionListFilterPrepareOptions() {
  const state: IRootState = yield select();
  const organizationsSelectOptions:
    | IDictionaryKnoOrganizationSelectOption[]
    | undefined = getDictionariesKnoOrganizationsSelectOptions(state);
  const supervisionTypesSelectOptions: IDictionarySupervisionTypeSelectOption[] = [];
  const decisionStatusesSelectOptions: IKnmStatusSelectOption[] = getCatalogDecisionStatusesSelectOptions(state);
  const getDictionariesCountriesSelectOptions = makeGetCatalogSelectOptions(CommonCatalogsNamesEnum.COUNTRIES);
  const countriesSelectOptions: ICountrySelectOption[] | undefined = getDictionariesCountriesSelectOptions(state);
  const getDictionariesGenericReasonsSelectOptions = makeGetCatalogSelectOptions(
    CommonCatalogsNamesEnum.GENERIC_REASONS,
  );
  const reasonsSelectOptions: IGenericReasonSelectOption[] | undefined = getDictionariesGenericReasonsSelectOptions(
    state,
  );
  const getDictionariesTargetEntitySelectOptions = makeGetDictionariesTargetEntitySelectOptions();
  const targetEntitiesSelectOptions:
    | IDictionaryTargetEntitySelectOption[]
    | undefined = getDictionariesTargetEntitySelectOptions(state, undefined)?.filter(
    targetEntityOption => targetEntityOption.code !== null,
  );

  const subjectTypesSelectOptions: IDictionarySubjectTypeSelectOption[] | undefined = [];
  const subjectKindsSelectOptions: IDictionarySubjectKindSelectOption[] | undefined = [];
  const subjectSubKindsSelectOptions: IDictionarySubjectSubKindSelectOption[] | undefined = [];
  const riskAndDanderCategoriesSelectOptions:
    | IDictionaryRiskCategorySelectOption[]
    | undefined = getDictionariesRiskCategoriesSelectOptions(state, undefined);

  const riskCategoriesSelectOptions = riskAndDanderCategoriesSelectOptions?.filter(item => !item.code.startsWith('0'));
  const dangerClassesSelectOptions = riskAndDanderCategoriesSelectOptions?.filter(item => item.code.startsWith('0'));

  const kindsSelectOptions:
    | IDictionarySelectOption<IDecisionKind>[]
    | undefined = getDictionariesDecisionKindsAllSelectOptions(state);

  const userProsecutorOrganisationsSelectOptions:
    | IUserOrganisationSelectOption[]
    | undefined = getUserProsecutorSelectOptions(state);

  const territorialUnitsSelectOptions: ITerritorialUnitSelectOption[] = getCatalogsTerritorialUnitsSelectOptions(state);

  return {
    organizationsSelectOptions,
    supervisionTypesSelectOptions,
    decisionStatusesSelectOptions,
    countriesSelectOptions,
    reasonsSelectOptions,
    targetEntitiesSelectOptions,
    subjectTypesSelectOptions,
    subjectKindsSelectOptions,
    subjectSubKindsSelectOptions,
    riskCategoriesSelectOptions,
    dangerClassesSelectOptions,
    kindsSelectOptions,
    userProsecutorOrganisationsSelectOptions,
    territorialUnitsSelectOptions,
  };
}

// Сага для загрузки данных о субъектах
function* fetchSubjectDataSaga(ids?: string[], stids?: string[]) {
  const state: IRootState = yield select();

  if (ids?.length && stids?.length) {
    // Запускаем параллельные запросы
    const subjectTypesStatus = getDictionariesSubjectTypesMultiStatus(state, ids, stids);
    const subjectKindsStatus = getDictionariesSubjectKindsMultiStatus(state, ids, stids);
    const subjectSubKindsStatus = getDictionariesSubjectSubKindsMultiStatus(state, ids, stids);

    if (
      (!subjectTypesStatus || subjectTypesStatus === DictionaryStatusesEnum.IDLE) &&
      (!subjectKindsStatus || subjectKindsStatus === DictionaryStatusesEnum.IDLE) &&
      (!subjectSubKindsStatus || subjectSubKindsStatus === DictionaryStatusesEnum.IDLE)
    ) {
      yield all([
        put(dictionariesSubjectTypesMultiItemGet(ids, stids)),
        put(dictionariesSubjectKindsMultiItemGet(ids, stids)),
        put(dictionariesSubjectSubKindsMultiItemGet(ids, stids)),
      ]);

      yield all([
        take([
          DictionariesActionsEnum.DICTIONARIES_SUBJECT_TYPE_MULTI_ITEM_GET_SUCCESS,
          DictionariesActionsEnum.DICTIONARIES_SUBJECT_TYPE_MULTI_ITEM_GET_FAILURE,
        ]),
        take([
          DictionariesActionsEnum.DICTIONARIES_SUBJECT_KIND_MULTI_ITEM_GET_SUCCESS,
          DictionariesActionsEnum.DICTIONARIES_SUBJECT_KIND_MULTI_ITEM_GET_FAILURE,
        ]),
        take([
          DictionariesActionsEnum.DICTIONARIES_SUBJECT_SUB_KIND_MULTI_ITEM_GET_SUCCESS,
          DictionariesActionsEnum.DICTIONARIES_SUBJECT_SUB_KIND_MULTI_ITEM_GET_FAILURE,
        ]),
      ]);
    }
  }
}

function* decisionListFilterUpdateFilterByOptions(
  raw: IDecisionListFilterRaw,
  ids: string[] | undefined,
  stids: string[] | undefined,
  options: IDictionarySupervisionTypeSelectOption[] | undefined,
  filter: IDecisionListFilter,
  supervisionType: IDictionarySupervisionTypeSelectOption | undefined,
) {
  const state: IRootState = yield select();

  const getDictionariesSubjectTypesMultiSelectOptions = makeGetDictionariesSubjectTypesMultiSelectOptions();
  const getDictionariesSubjectKindsMultiSelectOptions = makeGetDictionariesSubjectKindsMultiSelectOptions();
  const getDictionariesSubjectSubKindsMultiSelectOptions = makeGetDictionariesSubjectSubKindsMultiSelectOptions();

  let copyOptions = options ? [...options] : [];
  let copyFilter = { ...filter };

  if (ids?.length) {
    if (!copyOptions?.length) {
      copyOptions = yield dictionariesSupervisionTypesItemGetHandlerWithReturnOptions(ids);
    }

    copyFilter = {
      ...filter,
      supervisionType,
    };

    const stoptions: IDictionarySubjectTypeSelectOption[] | undefined = getDictionariesSubjectTypesMultiSelectOptions(
      state,
      ids || [],
      stids || [],
    );
    const skoptions: IDictionarySubjectKindSelectOption[] | undefined = getDictionariesSubjectKindsMultiSelectOptions(
      state,
      ids || [],
      stids || [],
    );
    const sskoptions:
      | IDictionarySubjectSubKindSelectOption[]
      | undefined = getDictionariesSubjectSubKindsMultiSelectOptions(state, ids || [], stids || []);

    if (stids?.length) {
      copyFilter = {
        ...copyFilter,
        objectTypes: stoptions?.filter((subjectTypeSelectOption: IDictionarySubjectTypeSelectOption) =>
          raw?.objectTypeIds?.find(objectTypeCode => objectTypeCode === subjectTypeSelectOption?.id),
        ),
        objectKinds: skoptions?.filter((subjectKindSelectOption: IDictionarySubjectKindSelectOption) =>
          raw?.objectKindIds?.find(subjectKindId => subjectKindId === subjectKindSelectOption?.value?.dictVersionId),
        ),
        objectSubKinds: sskoptions?.filter((subjectSubKindSelectOption: IDictionarySubjectSubKindSelectOption) =>
          raw?.objectSubKindIds?.find(
            subjectSubKindId => subjectSubKindId === subjectSubKindSelectOption?.value?.dictVersionId,
          ),
        ),
      };
    }
  }

  return copyFilter;
}

function* decisionListFilterGetDataSaga(action: ActionType<typeof decisionListFilterGetData>) {
  function* funcTry() {
    const { id } = action.payload;
    const state: IRootState = yield select();

    const raw: IDecisionListFilterRaw = yield fetchDecisionFilterById(id);

    const selectOptions: IDecisionListFilterSelectOptions = yield call(decisionListFilterPrepareOptions);

    const {
      organizationsSelectOptions,
      supervisionTypesSelectOptions,
      decisionStatusesSelectOptions,
      countriesSelectOptions,
      reasonsSelectOptions,
      targetEntitiesSelectOptions,
      subjectTypesSelectOptions,
      subjectKindsSelectOptions,
      subjectSubKindsSelectOptions,
      riskCategoriesSelectOptions,
      dangerClassesSelectOptions,
      kindsSelectOptions,
      userProsecutorOrganisationsSelectOptions,
      territorialUnitsSelectOptions,
    } = selectOptions || {};

    const filter = mapRawToDecisionFilter({
      raw,
      organizationsSelectOptions,
      supervisionTypesSelectOptions,
      decisionStatusesSelectOptions,
      countriesSelectOptions,
      reasonsSelectOptions,
      targetEntitiesSelectOptions,
      subjectTypesSelectOptions,
      subjectKindsSelectOptions,
      subjectSubKindsSelectOptions,
      riskCategoriesSelectOptions,
      dangerClassesSelectOptions,
      kindsSelectOptions,
      territorialUnitsSelectOptions,
      userProsecutorOrganisationsSelectOptions: userProsecutorOrganisationsSelectOptions ?? [],
    });

    const ids = convertFilterDataToControlOrganizationsIds(
      organizationsSelectOptions,
      filter.controllingOrganizations || [],
    );

    const getDictionariesSupervisionTypesSelectOptions = makeGetDictionariesSupervisionTypesExtendedSelectOptions();
    const options: IDictionarySupervisionTypeSelectOption[] | undefined = getDictionariesSupervisionTypesSelectOptions(
      state,
      ids || [],
      false,
      false,
    );

    const supervisionType = options?.find(
      (supervisionTypeSelectOption: IDictionarySupervisionTypeSelectOption) =>
        supervisionTypeSelectOption?.code === raw?.controlKindCode,
    );

    const stids = convertFilterDataToSupervisionTypesIds(options, supervisionType ? [supervisionType] : []);

    yield call(fetchSubjectDataSaga, ids, stids);

    const updatedFilter: IDecisionListFilter = yield call(
      decisionListFilterUpdateFilterByOptions,
      raw,
      ids,
      stids,
      options,
      filter,
      supervisionType,
    );

    yield put(decisionListFilterSetCurrentControlOrganizations(ids || []));
    yield put(decisionListFilterSetCurrentSupervisionTypes(stids || []));
    yield put(decisionListFilterSetData(id, updatedFilter));
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* decisionListFilterCreateSaga(action: ActionType<typeof decisionListFilterCreate>) {
  function* funcTry() {
    const { data } = action.payload;
    const state: IRootState = yield select();
    const rawFilter = mapDecisionFilterToRaw(data);
    const meta: IFilterResult = yield createDecisionFilter(rawFilter);
    const metaList = state.decisionListFilter.filters.map(f => f.meta);

    yield put(decisionListFilterSetMeta([...metaList, meta]));
    yield put(decisionListFilterSetData(meta.id, data));
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* decisionListFilterUpdateSaga(action: ActionType<typeof decisionListFilterUpdate>) {
  function* funcTry() {
    const { id, data } = action.payload;
    const rawFilter = mapDecisionFilterToRaw(data);

    yield updateDecisionFilter(id, rawFilter);
    yield put(decisionListFilterSetData(id, data));
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* decisionListFilterRemoveSaga(action: ActionType<typeof decisionListFilterRemove>) {
  yield sagaWrapper({
    *funcTry() {
      const { id } = action.payload;

      yield deleteDecisionFilter(id);
    },
  });
}

function* decisionListFilterSetCurrentControlOrganizationsSaga(
  action: ActionType<typeof decisionListFilterSetCurrentControlOrganizations>,
) {
  const { ids } = action.payload;

  if (ids.length) {
    yield put(dictionariesSupervisionTypesItemGet(ids, false));

    const state: IRootState = yield select();
    const supervisionTypes = getDecisionListCurrentSupervisionTypes(state);

    if (supervisionTypes.length) {
      yield put(dictionariesSubjectTypesMultiItemGet(ids, supervisionTypes));
      yield put(dictionariesSubjectKindsMultiItemGet(ids, supervisionTypes));
      yield put(dictionariesSubjectSubKindsMultiItemGet(ids, supervisionTypes));
    }
  }
}

function* decisionListFilterSetCurrentSupervisionTypesSaga(
  action: ActionType<typeof decisionListFilterSetCurrentSupervisionTypes>,
) {
  const { ids } = action.payload;

  const state: IRootState = yield select();
  const controllingOrganizations = getDecisionListCurrentControlOrganizations(state);

  if (controllingOrganizations.length && ids.length) {
    yield put(dictionariesSubjectTypesMultiItemGet(controllingOrganizations, ids));
    yield put(dictionariesSubjectKindsMultiItemGet(controllingOrganizations, ids));
    yield put(dictionariesSubjectSubKindsMultiItemGet(controllingOrganizations, ids));
  }
}
